import {Invoice} from "../../entity/model/invoice";
import {BrandingTemplate} from "../../entity/model/branding-template";
import {InvoiceUploadContext} from "../../entity/props/title/invoice-dialog-props";
import {Section} from "../../entity/resource/section";
import InvoiceTemplateStore from "../store/invoice-template-store";
import {RequestBody} from "../../entity/core/request-body";
import {InvoiceRequest} from "../../entity/model/invoice-request";
import {pdf} from '@react-pdf/renderer';
import React from 'react';
import InvoiceDocument from "../../app/layout/components/report/InvoiceDocument";
import {toast} from "react-toastify";

export class InvoiceTemplateService {
    private static readonly DEFAULT_DUE_DAYS = 30;
    private invoiceTemplateStore: InvoiceTemplateStore;

    constructor(invoiceTemplateStore: InvoiceTemplateStore) {
        this.invoiceTemplateStore = invoiceTemplateStore;}

    async generateInvoicePDF(values: Invoice, template: BrandingTemplate): Promise<Uint8Array | undefined> {
        const pdfBlob = await pdf(
            React.createElement(InvoiceDocument, {
                values: values,
                template: template
            }) as any
        ).toBlob();

        const arrayBuffer = await pdfBlob.arrayBuffer();
        const pdfBytes = new Uint8Array(arrayBuffer);

        return pdfBytes;
    }

    async uploadToInvoicesSection(
        pdfBytes: Uint8Array,
        filename: string,
        uploadContext: InvoiceUploadContext,
        uploadFileFunction: (organizationId: string, file: File, titleId?: string, sectionId?: string, setFileProgress?: (progress: number) => void, titleTemplateId?: string) => Promise<any>,
        refreshTitleFunction?: (organizationId: string, titleId: string) => Promise<any>,
        refreshTemplateFunction?: (organizationId: string, templateId: string, forceRefresh?: boolean) => Promise<any>
    ): Promise<{ documentUrl?: string, documentId?: string, filename?: string } | undefined> {
        const invoicesSection = this.findInvoicesSection(uploadContext.sections);

        if (!invoicesSection) {
            const availableSections = uploadContext.sections.map(s => s.name).join(', ');
            toast.error(`Invoices section not found. Available sections: ${availableSections}. Please create an 'Invoices' section first.`);
            return;
        }

        const uploadResult = await this.performFileUpload(
            pdfBytes,
            filename,
            uploadContext,
            invoicesSection,
            uploadFileFunction,
            refreshTitleFunction,
            refreshTemplateFunction);

        return uploadResult;
    }

    async generateAndProcessInvoice(
        values: Invoice,
        template: BrandingTemplate,
        uploadContext: InvoiceUploadContext,
        uploadFileFunction: (organizationId: string, file: File, titleId?: string, sectionId?: string, setFileProgress?: (progress: number) => void, titleTemplateId?: string) => Promise<any>,
        refreshTitleFunction?: (organizationId: string, titleId: string) => Promise<any>,
        refreshTemplateFunction?: (organizationId: string, templateId: string, forceRefresh?: boolean) => Promise<any>,
        invoiceTemplateId?: string
    ): Promise<{ documentUrl?: string, documentId?: string } | undefined> {
        const pdfBytes = await this.generateInvoicePDF(values, template);
        if (!pdfBytes) {
            return;
        }

        const filename = `Invoice-${values.orderNumber}-${values.invoiceNumber}.pdf`;

        const uploadResult = await this.uploadToInvoicesSection(
            pdfBytes,
            filename,
            uploadContext,
            uploadFileFunction,
            refreshTitleFunction,
            refreshTemplateFunction
        );

        if (uploadResult) {
            const documentId = uploadResult.documentId;
            const projectTitleID = uploadContext.titleId ?? '';
            await this.saveInvoiceTemplate(values, template, uploadContext.organizationId, projectTitleID, documentId, invoiceTemplateId);
            toast.success('Invoice generated and uploaded successfully!');

            return {
                documentUrl: uploadResult.documentUrl,
                documentId: uploadResult.documentId
            };
        }

        toast.warning('Invoice generated but upload to Invoices section failed.');
        return;
    }

    getUploadContext(sections: Section[], organizationId: string, titleId?: string, templateId?: string): InvoiceUploadContext | undefined {
        if (!organizationId) {
            return;
        }

        if (!titleId && !templateId) {
            return;
        }

        if (sections.length === 0) {
            return;
        }

        return {sections, organizationId, titleId, templateId};
    }

    getInvoiceTemplateByDocumentId(documentId: string): InvoiceRequest | undefined {
        return this.invoiceTemplateStore.getInvoiceTemplate(documentId);
    }

    getInitialValues(
        invoiceTemplate: InvoiceRequest | null | undefined,
        orderData: any,
        brandingTemplates: BrandingTemplate[]
    ): Invoice {
        const defaultDueDate = new Date(Date.now() + InvoiceTemplateService.DEFAULT_DUE_DAYS * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        return {
            invoiceNumber: invoiceTemplate ? invoiceTemplate.invoiceNumber : `INV-${Date.now().toString().slice(-6)}`,
            fee: invoiceTemplate ? invoiceTemplate.serviceFee.toString() : '',
            receiptsCopyCosts: invoiceTemplate ? invoiceTemplate.receiptsCopyCosts.toString() : '',
            dueDate: invoiceTemplate ? invoiceTemplate.dueDate : defaultDueDate,
            notes: invoiceTemplate ? invoiceTemplate.additionalNotes ?? '' : '',
            selectedTemplate: invoiceTemplate ? brandingTemplates.find((template) => template.id === invoiceTemplate?.brandingTemplateID)?.id ?? (brandingTemplates.length > 0 ? brandingTemplates[0].id : 'default') : (brandingTemplates.length > 0 ? brandingTemplates[0].id : 'default'),
            orderNumber: invoiceTemplate ? invoiceTemplate.orderNumber : orderData.orderNumber,
            propertyAddress: invoiceTemplate ? invoiceTemplate.propertyAddress : orderData.propertyAddress,
            parcelNumber: invoiceTemplate ? invoiceTemplate.parcelNumber : orderData.parcelNumber,
            clientName: invoiceTemplate ? invoiceTemplate.clientName : orderData.clientName,
        };
    }

    async loadInvoiceTemplates(organizationId: string, titleId: string): Promise<void> {
        await this.invoiceTemplateStore.loadInvoiceTemplates(organizationId, titleId);
    }

    private async saveInvoiceTemplate(values: Invoice, template: BrandingTemplate, organizationId: string, projectTitleID: string, documentId?: string, invoiceTemplateId?: string): Promise<InvoiceRequest | undefined> {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const uniqueTemplateName = `Invoice-${values.invoiceNumber}-${timestamp}`;

        const payload = {
            projectTitleID: projectTitleID,
            brandingTemplateID: template.id,
            templateName: uniqueTemplateName,
            oldTemplateID: null,
            parentInvoiceTemplateID: null,
            documentID: documentId ?? null,
            invoiceNumber: values.invoiceNumber,
            serviceFee: parseFloat(values.fee ?? '0'),
            receiptsCopyCosts: parseFloat(values.receiptsCopyCosts ?? '0'),
            dueDate: values.dueDate,
            additionalNotes: values.notes,
            orderNumber: values.orderNumber,
            propertyAddress: values.propertyAddress,
            parcelNumber: values.parcelNumber,
            clientName: values.clientName
        };

        const requestBody: RequestBody = {body: payload};

        let result;
        if (invoiceTemplateId)
            result = await this.invoiceTemplateStore.updateInvoiceTemplate(organizationId, invoiceTemplateId, requestBody);
        else
            result = await this.invoiceTemplateStore.createInvoiceTemplate(organizationId, requestBody);

        if (typeof result === 'string') {
            toast.error(result);
            return;
        }

        return result;
    }

    private findInvoicesSection(sections: Section[]): Section | undefined {
        const possibleInvoiceSectionNames = ['Invoices', 'Invoice', 'INVOICES', 'INVOICE'];

        for (const sectionName of possibleInvoiceSectionNames) {
            const section = sections.find(section => section.name.toLowerCase() === sectionName.toLowerCase());
            if (section) return section;
        }

        return sections.find(section => section.name.toLowerCase().includes('invoice'));
    }

    private async performFileUpload(
        pdfBytes: Uint8Array,
        filename: string,
        context: InvoiceUploadContext,
        invoicesSection: Section,
        uploadFileFunction: (organizationId: string, file: File, titleId?: string, sectionId?: string, setFileProgress?: (progress: number) => void, titleTemplateId?: string) => Promise<any>,
        refreshTitleFunction?: (organizationId: string, titleId: string) => Promise<any>,
        refreshTemplateFunction?: (organizationId: string, templateId: string, forceRefresh?: boolean) => Promise<any>
    ): Promise<{ documentUrl?: string, documentId?: string, filename?: string } | undefined> {
        const pdfFile = new File([pdfBytes], filename, {
            type: 'application/pdf', lastModified: Date.now()
        });

        if (context.titleId) {
            await uploadFileFunction(
                context.organizationId,
                pdfFile,
                context.titleId,
                invoicesSection.id,
                undefined,
                undefined
            );

            if (refreshTitleFunction) {
                const updatedTitle = await refreshTitleFunction(context.organizationId, context.titleId);
                if (updatedTitle) {
                    const updatedInvoicesSection = this.findInvoicesSection(updatedTitle.sections);
                    const uploadedDocument = updatedInvoicesSection?.documents
                        .find(doc => doc.name.includes(filename.replace('.pdf', '')));

                    return {
                        documentUrl: uploadedDocument?.url,
                        documentId: uploadedDocument?.id,
                        filename
                    };
                }
            }
        } else if (context.templateId) {
            const uploadResult = await uploadFileFunction(
                context.organizationId,
                pdfFile,
                undefined,
                invoicesSection.id,
                undefined,
                context.templateId
            );

            if (uploadResult) {
                return {
                    documentUrl: uploadResult.url,
                    documentId: uploadResult.id,
                    filename
                };
            }
        }

        toast.error('Upload completed but could not retrieve document URL');
        return;
    }
}